/* eslint-disable no-param-reassign */
/* eslint-disable prefer-destructuring */
import React, { FC, useRef, useState } from 'react'
import {
  Button,
  CloseButton,
  Flex,
  Text,
  Box,
  Checkbox,
  Badge,
  Divider,
  IconButton
} from '@chakra-ui/react'
import { v4 as uuidV4 } from 'uuid'
import { useTranslation } from 'react-i18next'
import { FiEdit, FiGrid, FiPlus, FiSave, FiTrash2 } from 'react-icons/fi'
import { useDrag, useDrop } from 'react-dnd'
import { Input } from '../../Form/Input'
import { useDnd } from '../../../hooks/useDnd'

interface Options {
  pos: number
  label: string
  value: string
}

interface InputBuildProps {
  data: {
    id: string
    name: string
    isRequired: boolean
    placeholder: string
    type: string
    value: string
    defaultValue: string
    label: string
    options?: Array<Options>
    error?: boolean
  }
  onExclude?: () => void
  indexList: number
  index: number
}

interface DropAndDragItem {
  type: string
  id: string | number
  index: number
  name: string
  indexList: number
}

type TypeOptionsData = Array<Options>

export const SelectBuild: FC<InputBuildProps> = ({
  data,
  onExclude,
  indexList,
  index
}) => {
  const [t] = useTranslation('pageCreatingForms')
  const [isRequired, setIsRequired] = useState(data.isRequired)
  const [placeholder, setPlaceholder] = useState(data.placeholder)
  const [defaultValue, setDefaultValue] = useState(data.defaultValue)
  const [label, setLabel] = useState(data.label)
  const [showEdit, setShowEdit] = useState(false)
  const [error, setError] = useState(data?.error)
  const [options, setOptions] = useState<TypeOptionsData>(() => {
    if (data?.options !== undefined) {
      if (data?.options.length > 0) {
        return data?.options
      }
    }

    return [
      {
        label: '',
        pos: 0,
        value: ''
      }
    ]
  })

  const handlerAddNewOption = (i: number) => {
    setOptions([
      ...options,
      {
        label: '',
        pos: i + 1,
        value: ''
      }
    ])
  }

  const handlerRemoveOption = (i: number) => {
    const values = [...options]
    values.splice(i, 1)

    const newValues = values.map((option, indexOption) => {
      return {
        pos: indexOption + 1,
        label: option.label,
        value: option.value
      }
    })

    setOptions(newValues)
  }

  const handlerInputChangeLabel = (
    event: React.ChangeEvent<HTMLInputElement>,
    i: number
  ) => {
    const values = [...options]
    const valuesAux = values.slice(i, i + 1)
    const newValue = valuesAux.map((option) => {
      return {
        pos: option.pos,
        label: event.currentTarget.value,
        value: event.currentTarget.value
      }
    })

    const valuesChange = values
    valuesChange[i] = newValue[0]

    setOptions(values)
  }

  const { MoveForm, listForms, setListForms } = useDnd()
  const ref = useRef<any>(null)
  const [{ isDragging }, dragRef] = useDrag({
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    }),
    type: 'GROUP',
    item: { type: 'GROUP', id: data?.id, index, name: data?.name, indexList }
  })

  const [, dropRef] = useDrop({
    accept: 'GROUP',
    hover(item: DropAndDragItem, monitor) {
      const draggedListIndex = item.indexList
      const targetListIndex = indexList

      const draggedIndex = item.index
      const targetIndex = index

      if (
        draggedIndex === targetIndex &&
        draggedListIndex === targetListIndex
      ) {
        return
      }

      const targetSize = ref?.current.getBoundingClientRect()
      const targetCenter = (targetSize.bottom - targetSize.top) / 2

      const draggedOffset = monitor.getClientOffset()
      const coordY = draggedOffset !== undefined ? draggedOffset?.y : 1
      const coordX = draggedOffset !== undefined ? draggedOffset?.x : 1
      const draggedTop = coordY !== undefined ? coordY - targetSize.top : 1

      if (draggedIndex < targetIndex && draggedTop < targetCenter) {
        return
      }

      if (draggedIndex > targetIndex && draggedTop > targetCenter) {
        return
      }

      MoveForm(draggedListIndex, targetListIndex, draggedIndex, targetIndex)

      item.index = targetIndex
      item.indexList = targetListIndex
    }
  })

  dragRef(dropRef(ref))

  // return error options jsx
  const returnOptionsError = () => {
    let isError = false

    options?.forEach((option) => {
      if (option?.value === '') {
        isError = true
      }
    })

    if (isError) {
      return (
        <Badge colorScheme="red" fontSize="10px">
          {t('errors.type.2')}
        </Badge>
      )
    }

    return null
  }

  // valid form
  const internalErrorForm = () => {
    let isError = false

    if (label === '') {
      isError = true
      return isError
    }

    options?.forEach((option) => {
      if (option.value === '') {
        isError = true
        return isError
      }
      return isError
    })

    return isError
  }

  const handlerChangeFieldValue = () => {
    const internalError = internalErrorForm()

    const group = listForms[indexList]
    const object = {
      isRequired,
      defaultValue,
      name: uuidV4(),
      label,
      placeholder,
      options,
      error: internalError
    }

    const fields = group.fields.map((field) => {
      return field.id === data.id ? { ...field, ...object } : field
    })

    const objectField = {
      fields
    }

    const newGroupValues = listForms.map((groupList, indexGroup) => {
      return indexList === indexGroup
        ? { ...groupList, ...objectField }
        : groupList
    })

    return newGroupValues
  }

  return (
    <Box
      w="100%"
      ref={ref}
      style={isDragging ? { opacity: 0.4 } : undefined}
      border={error ? '1px solid #E53E3E' : '1px solid #E2E8F0'}
      borderRadius={8}
      bg="white"
      overflow="hidden"
      boxShadow="sm"
    >
      {/* Field header row */}
      <Flex
        w="100%"
        px="4"
        py="3"
        justifyContent="space-between"
        alignItems="center"
        bg={error ? 'red.50' : 'gray.50'}
        borderBottom={showEdit ? '1px solid #E2E8F0' : 'none'}
      >
        <Flex gap={3} alignItems="center" flex={1} minW={0}>
          <Box color="gray.400" cursor="grab" flexShrink={0}>
            <FiGrid size={18} />
          </Box>
          <Text
            fontWeight="medium"
            fontSize="sm"
            color={label === '' ? 'gray.400' : 'gray.700'}
            noOfLines={1}
          >
            {label === '' ? t('errors.type.1') : label}
          </Text>
          <Badge colorScheme="teal" fontSize="10px" flexShrink={0}>
            {t('fields.3')}
          </Badge>
          <Text fontSize="xs" color="gray.400" flexShrink={0}>
            {options.length === 1
              ? `${options.length} ${t('option')}`
              : `${options.length} ${t('options')}`}
          </Text>
          {isRequired && (
            <Badge colorScheme="orange" fontSize="10px" flexShrink={0}>
              {t('isRequired')}
            </Badge>
          )}
          {returnOptionsError()}
        </Flex>

        <Flex gap={2} flexShrink={0} ml={3}>
          <Button
            colorScheme={showEdit ? 'blue' : 'green'}
            size="xs"
            leftIcon={showEdit ? <FiSave /> : <FiEdit />}
            onClick={() => {
              setShowEdit(!showEdit)
              if (showEdit) {
                const newValuesGroups = handlerChangeFieldValue()
                setListForms(newValuesGroups)
              }
            }}
          >
            {showEdit ? t('saveEdit') : ''}
          </Button>
          <CloseButton
            size="sm"
            bg="red.500"
            color="white"
            borderRadius="md"
            _hover={{ bg: 'red.600' }}
            onClick={() => onExclude?.()}
          />
        </Flex>
      </Flex>

      {/* Edit panel */}
      {showEdit && (
        <Box px="4" py="4">
          <Flex direction="column" gap={4}>
            <Input
              name="label"
              label={`${t('question')}`}
              value={label}
              onChange={(event) => setLabel(event.target.value)}
            />
            <Flex alignItems="center" gap={3}>
              <Checkbox
                id={`required-${data.id}`}
                name="isRequired"
                defaultChecked={isRequired}
                colorScheme="green"
                onChange={(event) => setIsRequired(event.target.checked)}
              />
              <Text
                as="label"
                htmlFor={`required-${data.id}`}
                fontSize="sm"
                color="gray.600"
                cursor="pointer"
                userSelect="none"
              >
                {t('questionIsRequired')}
              </Text>
            </Flex>

            <Divider />

            <Box>
              <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={3}>
                {t('configOptions')}
              </Text>
              <Flex direction="column" gap={3}>
                {options.map((option, indexOption) => (
                  <Flex
                    key={option.pos}
                    alignItems="flex-end"
                    gap={3}
                  >
                    <Flex
                      w="28px"
                      h="28px"
                      borderRadius="full"
                      bgColor="teal.500"
                      alignItems="center"
                      justifyContent="center"
                      flexShrink={0}
                      mb="1"
                    >
                      <Text color="white" fontSize="xs" fontWeight="bold">
                        {indexOption + 1}
                      </Text>
                    </Flex>
                    <Box flex={1} maxW="320px">
                      <Input
                        name="labelSelect"
                        value={option.label}
                        label="Item"
                        onChange={(event) =>
                          handlerInputChangeLabel(event, indexOption)
                        }
                      />
                    </Box>
                    <IconButton
                      aria-label="Adicionar opção"
                      icon={<FiPlus />}
                      colorScheme="green"
                      size="sm"
                      mb="1"
                      onClick={() => handlerAddNewOption(indexOption)}
                    />
                    {options.length > 1 && (
                      <IconButton
                        aria-label="Remover opção"
                        icon={<FiTrash2 />}
                        colorScheme="red"
                        variant="ghost"
                        size="sm"
                        mb="1"
                        onClick={() => handlerRemoveOption(indexOption)}
                      />
                    )}
                  </Flex>
                ))}
              </Flex>
            </Box>
          </Flex>
        </Box>
      )}
    </Box>
  )
}
